import { useEffect, useState } from "react";

const MINIMUM_PUBLIC_WAITLIST_COUNT = 101;

const parseCount = (value: string | undefined) => {
  if (!value) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

let cachedLiveCount: number | null = null;
let liveCountRequest: Promise<number | null> | null = null;

const requestLiveCount = (fresh = false) => {
  if (!fresh && cachedLiveCount !== null) {
    return Promise.resolve(cachedLiveCount);
  }
  if (!fresh && liveCountRequest) return liveCountRequest;

  const request = fetch(`/api/waitlist${fresh ? "?fresh=1" : ""}`, {
    headers: { Accept: "application/json" },
    cache: fresh ? "no-store" : "default"
  })
    .then(async (response) => {
      if (!response.ok) return null;
      const payload = (await response.json()) as { count?: unknown };
      return typeof payload.count === "number" && payload.count >= 0
        ? payload.count
        : null;
    })
    .catch(() => null)
    .then((count) => {
      if (count !== null) cachedLiveCount = count;
      return count;
    })
    .finally(() => {
      liveCountRequest = null;
    });

  if (!fresh) liveCountRequest = request;
  return request;
};

const readDemoCount = () => {
  try {
    const entries = JSON.parse(
      window.localStorage.getItem("offr-waitlist-demo") ?? "[]"
    ) as Array<{ email?: unknown }>;
    return new Set(
      entries
        .map((entry) =>
          typeof entry.email === "string" ? entry.email.toLowerCase() : ""
        )
        .filter(Boolean)
    ).size;
  } catch {
    return 0;
  }
};

type WaitlistTallyProps = {
  compact?: boolean;
};

export function WaitlistTally({ compact = false }: WaitlistTallyProps) {
  const configuredCount = parseCount(import.meta.env.VITE_WAITLIST_COUNT);
  const demoMode =
    import.meta.env.VITE_WAITLIST_MODE === "demo" || import.meta.env.DEV;
  const [count, setCount] = useState<number | null>(configuredCount);

  useEffect(() => {
    const refresh = (fresh = false) => {
      if (demoMode) {
        setCount(readDemoCount());
        return;
      }
      void requestLiveCount(fresh).then((nextCount) => {
        if (nextCount !== null) setCount(nextCount);
      });
    };

    refresh();
    const onSubmitted = () => refresh(true);
    window.addEventListener("offr:waitlist-submitted", onSubmitted);
    return () =>
      window.removeEventListener("offr:waitlist-submitted", onSubmitted);
  }, [demoMode]);

  if (count === null || count < MINIMUM_PUBLIC_WAITLIST_COUNT) return null;

  const formatted = new Intl.NumberFormat().format(count);
  const message = `${formatted} people have signed up`;

  return (
    <div
      className={`waitlist-tally ${compact ? "waitlist-tally--compact" : ""}`.trim()}
      aria-label={message}
      aria-live="polite"
    >
      <span className="waitlist-tally__ticks" aria-hidden="true">
        <i /><i /><i /><i /><i />
      </span>
      <strong>{message}</strong>
    </div>
  );
}
